/**
 * Shared Forge connection-repo types.
 * Four catalog tabs — see CONNECTIONS.md.
 */

export type ConnectionKind = "hf" | "cloudflare" | "civitai" | "forge_endpoint";
export type ConnectionTab = ConnectionKind | "production";

export type ConnectionPurpose = "chat" | "tts" | "image" | "video" | "vision" | "music";

export type AppId =
  | "bookforge"
  | "textforge"
  | "agentforge"
  | "visionforge"
  | "videoforge"
  | "imageforge"
  | "voiceforge"
  | "skinforge"
  | "coldforge"
  | "musicforge"
  | "chatforge";

export const CONNECTION_PURPOSES: readonly ConnectionPurpose[] = [
  "chat",
  "tts",
  "image",
  "video",
  "vision",
  "music",
] as const;

export const CONNECTION_KINDS: readonly ConnectionKind[] = [
  "hf",
  "cloudflare",
  "civitai",
  "forge_endpoint",
] as const;

/** Catalog pref — not a forge_connections row. */
export const CATALOG_ID_RE = /^catalog:(hf|cloudflare|civitai):([^:]+):(.+)$/;

export function isCatalogConnectionId(id: string): boolean {
  return CATALOG_ID_RE.test(id || "");
}

export function parseCatalogConnectionId(id: string): {
  tab: "hf" | "cloudflare" | "civitai";
  provider: string;
  modelId: string;
} | null {
  const m = (id || "").match(CATALOG_ID_RE);
  if (!m) return null;
  return {
    tab: m[1] as "hf" | "cloudflare" | "civitai",
    provider: decodeURIComponent(m[2]),
    modelId: m[3],
  };
}

export function catalogConnectionId(
  tab: "hf" | "cloudflare" | "civitai",
  provider: string,
  modelId: string,
): string {
  return `catalog:${tab}:${encodeURIComponent(provider || tab)}:${modelId}`;
}

/** One list row = one SKU = one (model × engine) pair. Never collapse by hub id. */
export function catalogRowKey(m: {
  skuId?: string | null;
  modelId?: string | null;
  provider?: string | null;
  chatModelId?: string | null;
}): string {
  const sku = String(m.skuId || "").trim();
  if (sku) return sku;
  const model = String(m.modelId || "").trim();
  const provider = String(m.provider || "").trim();
  if (model && provider) return `${model}::${provider}`;
  return String(m.chatModelId || model || "");
}

/** Serving engine name for the picker. Empty when it would just repeat the tab. */
export function engineName(
  provider: string | null | undefined,
  tab?: string,
): string {
  const p = String(provider || "").trim();
  if (!p) return "";
  const tabName = String(tab || "").toLowerCase();
  if (p.toLowerCase() === tabName) return "";
  if (["hf", "huggingface", "hf-inference"].includes(p.toLowerCase())) return "";
  return p;
}

export function isConnectionPurpose(v: string): v is ConnectionPurpose {
  return (CONNECTION_PURPOSES as readonly string[]).includes(v);
}

export function isConnectionKind(v: string): v is ConnectionKind {
  return (CONNECTION_KINDS as readonly string[]).includes(v);
}

/** Public-safe list row. No bind/session tokens. */
export interface Connection {
  id: string;
  kind: ConnectionKind;
  sourceTable: string;
  sourceId: string;
  appOrigin: string | null;
  label: string;
  modelId: string | null;
  engine: string | null;
  modality: string;
  endpointUrl: string | null;
  status: string;
  ready: boolean;
  visibility: "private" | "public";
  lastHealthAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
  tokenLast4?: string | null;
  detail?: string | null;
  skuId?: string | null;
  /** CivitAI version base model (SDXL, Flux.1 D, …) when known. */
  baseModel?: string | null;
  civitaiType?: string | null;
  /** AccessHub tool this Connect assigned. GpuBrowser is per-tool, not per-app. */
  toolId?: string | null;
  dumpId?: string | null;
}

export interface ConnectionCreds {
  id: string;
  kind: ConnectionKind;
  modelId: string | null;
  provider?: string | null;
  endpointUrl: string | null;
  bindToken?: string | null;
  sessionToken?: string | null;
}

export type SelectedByPurpose = Partial<Record<ConnectionPurpose, string>>;

export interface ConnectionsListResponse {
  ok: true;
  connections: Connection[];
  selectedByPurpose: SelectedByPurpose;
  appId: string | null;
}

export interface SelectConnectionRequest {
  appId: AppId | string;
  purpose: ConnectionPurpose;
  connectionId: string;
}

export interface SelectConnectionResponse {
  ok: true;
  connection: Connection;
  needsWake?: boolean;
  selectedByPurpose: SelectedByPurpose;
}

export interface CatalogSnapshotMeta {
  tab: ConnectionKind;
  source: string;
  modelCount: number;
  fetchedAt: string;
  expiresAt: string;
  stale?: boolean;
  truncated?: boolean;
  error?: string | null;
  configured?: boolean;
}

export interface CatalogSnapshot<T = unknown> extends CatalogSnapshotMeta {
  payload: T;
}
